import React, { useEffect, useState } from 'react';
import { IoClose, IoAddOutline, IoCalendarOutline } from 'react-icons/io5';

interface DiaryEntry {
  id: number;
  userId: number;
  title: string;
  body: string;
  date: string;
  imageDataUrls: string[];
  createdAtIst: string;
  updatedAtIst: string;
}

interface DiaryListProps {
  userId: number;
  onClose: () => void;
  onSelectEntry: (entry: DiaryEntry) => void;
  onNewEntry: () => void;
}

export const DiaryList: React.FC<DiaryListProps> = ({ userId, onClose, onSelectEntry, onNewEntry }) => {
  const [entries, setEntries] = useState<DiaryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string>('');

  const fetchLatestEntries = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/diary/latest?userId=${userId}&take=15`);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch diary entries: ${response.status} ${errorText}`);
      }
      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error('Invalid response format from diary API');
      }
      setEntries(data);
    } catch (error: any) {
      console.error('Error fetching diary entries:', error);
      alert(`Failed to load diary entries: ${error.message || 'Unknown error'}`);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchLatestEntries();
  }, [userId, fetchLatestEntries]);

  const handleDateSearch = async () => {
    if (!selectedDate) {
      fetchLatestEntries();
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/diary/by-date?userId=${userId}&date=${selectedDate}`);
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setEntries([data]);
        } else {
          setEntries([]);
        }
      } else {
        const errorText = await response.text();
        throw new Error(`Failed to fetch diary entry: ${response.status} ${errorText}`);
      }
    } catch (error: any) {
      console.error('Error fetching diary by date:', error);
      alert(`Failed to load diary entry: ${error.message || 'Unknown error'}`);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.error('Invalid date string:', dateString);
        return dateString;
      }
      return date.toLocaleDateString('en-IN', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">My Diary</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <IoClose size={24} />
          </button>
        </div>

        <div className="p-4 border-b border-gray-700">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <IoCalendarOutline size={20} />
              </div>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full bg-gray-800 text-white rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Select date..."
              />
            </div>
            <button
              onClick={handleDateSearch}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Search
            </button>
            <button
              onClick={() => {
                setSelectedDate('');
                fetchLatestEntries();
              }}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400 mb-4">No diary entries yet</p>
              <button
                onClick={onNewEntry}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Create Your First Entry
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {entries.map((entry) => (
                <div
                  key={entry.id}
                  onClick={() => onSelectEntry(entry)}
                  className="bg-gray-800 rounded-lg p-4 cursor-pointer hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-white font-semibold text-lg flex-1 mr-2">
                      {entry.title || 'Untitled'}
                    </h3>
                    <span className="text-gray-400 text-sm whitespace-nowrap">
                      {formatDate(entry.date)}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm line-clamp-2">
                    {entry.body || 'No content'}
                  </p>
                  {entry.imageDataUrls && entry.imageDataUrls.length > 0 && (
                    <div className="mt-2 flex items-center text-xs text-gray-500">
                      <span className="mr-1">
                        <IoCalendarOutline size={14} />
                      </span>
                      {entry.imageDataUrls.length} image{entry.imageDataUrls.length > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-700">
          <button
            onClick={onNewEntry}
            className="w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 font-medium"
          >
            <IoAddOutline size={24} />
            New Diary Entry
          </button>
        </div>
      </div>
    </div>
  );
};
